/**
 * Animation Configuration System
 * Provides centralized configuration for all animations with performance monitoring and cross-browser optimization
 */

import { performanceOptimizer } from './performanceOptimizer';
import { getBrowserInfo, getAnimationCapabilities } from './browserCompatibility';

export type PerformanceLevel = 'high' | 'medium' | 'low';
export type AnimationTheme = 'default' | 'minimal' | 'dramatic';

export interface AnimationConfig {
  performance: PerformanceLevel;
  reducedMotion: boolean;
  theme: AnimationTheme;
  customTimings: TimingConfig;
  enableGPUAcceleration: boolean;
  maxConcurrentAnimations: number;
  enableComplexEffects: boolean;
  // Cross-browser specific settings
  browserName: string;
  browserVersion: number;
  enable3DTransforms: boolean;
  enableBackdropFilter: boolean;
  enableParallax: boolean;
  animationDurationMultiplier: number;
}

export interface TimingConfig {
  fast: number;
  normal: number;
  slow: number;
  defaultEasing: string | number[];
  springConfig: {
    tension: number;
    friction: number;
  };
}

export interface PerformanceMetrics {
  fps: number;
  memoryUsage: number;
  activeAnimations: number;
  droppedFrames: number;
}

// Get browser-optimized default configuration
const getBrowserOptimizedConfig = (): AnimationConfig => {
  const browserInfo = getBrowserInfo();
  const capabilities = getAnimationCapabilities();
  const optimizerSettings = performanceOptimizer.getSettings();

  return {
    performance: performanceOptimizer.getSettings().enableComplexAnimations ? 'high' : 'medium',
    reducedMotion: browserInfo.prefersReducedMotion,
    theme: 'default',
    customTimings: {
      fast: 0.2 * optimizerSettings.animationDurationMultiplier,
      normal: 0.4 * optimizerSettings.animationDurationMultiplier,
      slow: 0.8 * optimizerSettings.animationDurationMultiplier,
      defaultEasing: browserInfo.name === 'safari' 
        ? [0.25, 0.46, 0.45, 0.94] 
        : [0.4, 0, 0.2, 1],
      springConfig: {
        tension: browserInfo.name === 'safari' ? 360 : 300,
        friction: browserInfo.name === 'safari' ? 36 : 30,
      },
    },
    enableGPUAcceleration: capabilities.shouldUseGPUAcceleration,
    maxConcurrentAnimations: capabilities.maxConcurrentAnimations,
    enableComplexEffects: optimizerSettings.enableComplexAnimations,
    // Cross-browser specific settings
    browserName: browserInfo.name,
    browserVersion: browserInfo.version,
    enable3DTransforms: optimizerSettings.enable3DTransforms,
    enableBackdropFilter: optimizerSettings.enableBackdropFilter,
    enableParallax: optimizerSettings.enableParallax,
    animationDurationMultiplier: optimizerSettings.animationDurationMultiplier,
  };
};

// Default animation configuration (dynamically generated)
export const defaultAnimationConfig: AnimationConfig = getBrowserOptimizedConfig();

// Performance-based configuration presets
export const performanceConfigs: Record<PerformanceLevel, Partial<AnimationConfig>> = {
  high: {
    enableComplexEffects: true,
    maxConcurrentAnimations: 15,
    enableGPUAcceleration: true,
  },
  medium: {
    enableComplexEffects: true,
    maxConcurrentAnimations: 8,
    enableGPUAcceleration: true,
  },
  low: {
    enableComplexEffects: false,
    maxConcurrentAnimations: 3,
    enableGPUAcceleration: false,
  },
};

// Animation theme configurations
export const themeConfigs: Record<AnimationTheme, Partial<TimingConfig>> = {
  default: {
    fast: 0.2,
    normal: 0.4,
    slow: 0.8,
    defaultEasing: [0.4, 0, 0.2, 1],
  },
  minimal: {
    fast: 0.15,
    normal: 0.3,
    slow: 0.6,
    defaultEasing: 'easeOut',
  },
  dramatic: {
    fast: 0.3,
    normal: 0.6,
    slow: 1.2,
    defaultEasing: [0.68, -0.55, 0.265, 1.55],
  },
};

/**
 * Detects user's motion preferences and device capabilities
 */
export const detectMotionPreferences = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mediaQuery.matches;
};

/**
 * Detects device performance level based on hardware capabilities
 */
export const detectPerformanceLevel = (): PerformanceLevel => {
  if (typeof window === 'undefined') return 'medium';
  
  // Check for hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 4;
  
  // Check for device memory (if available)
  const memory = (navigator as any).deviceMemory || 4;
  
  // Check for connection speed
  const connection = (navigator as any).connection;
  const effectiveType = connection?.effectiveType || '4g';
  
  // Determine performance level based on device capabilities
  if (cores >= 8 && memory >= 8 && effectiveType === '4g') {
    return 'high';
  } else if (cores >= 4 && memory >= 4) {
    return 'medium';
  } else {
    return 'low';
  }
};

/**
 * Creates optimized animation configuration based on device, user preferences, and browser capabilities
 */
export const createOptimizedConfig = (): AnimationConfig => {
  const reducedMotion = detectMotionPreferences();
  const performanceLevel = detectPerformanceLevel();
  const browserInfo = getBrowserInfo();
  const capabilities = getAnimationCapabilities();
  const optimizerSettings = performanceOptimizer.getSettings();
  
  const baseConfig = getBrowserOptimizedConfig();
  const performanceOverrides = performanceConfigs[performanceLevel];
  
  return {
    ...baseConfig,
    ...performanceOverrides,
    performance: performanceLevel,
    reducedMotion,
    // Disable complex effects if reduced motion is preferred
    enableComplexEffects: reducedMotion ? false : (performanceOverrides.enableComplexEffects ?? optimizerSettings.enableComplexAnimations),
    // Browser-specific overrides
    enable3DTransforms: reducedMotion ? false : optimizerSettings.enable3DTransforms,
    enableBackdropFilter: optimizerSettings.enableBackdropFilter,
    enableParallax: reducedMotion ? false : optimizerSettings.enableParallax,
    maxConcurrentAnimations: Math.min(
      performanceOverrides.maxConcurrentAnimations ?? 10,
      capabilities.maxConcurrentAnimations
    ),
  };
};

/**
 * Get animation variants optimized for current browser
 */
export const getOptimizedAnimationVariants = () => {
  const config = createOptimizedConfig();
  
  return {
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    
    slideUp: config.enableComplexEffects ? {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 },
    } : {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    
    scale: config.enableComplexEffects ? {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.8 },
    } : {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    
    rotate3D: config.enable3DTransforms ? {
      initial: { opacity: 0, rotateY: -90 },
      animate: { opacity: 1, rotateY: 0 },
      exit: { opacity: 0, rotateY: 90 },
    } : {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
  };
};

/**
 * Refresh configuration when browser conditions change
 */
export const refreshAnimationConfig = (): AnimationConfig => {
  return createOptimizedConfig();
};